import { Component, OnInit } from '@angular/core';
import {
  UntypedFormBuilder, UntypedFormGroup, Validators
} from '@angular/forms';
import { combineLatest, merge } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { VALIDATORS } from 'app/constants';
import { AppCustomLine as CustomLine } from 'graphql/models/extended';
import { CoreDataService } from 'shared/services/core-data.service';
import { PositionCustomPropertiesService } from 'shared/services/position-custom-properties.service';
import { StoreService } from 'shared/services/store.service';
import { OfferBase } from 'models/offer.model';
import { SvgIconSizeMod } from 'models/layout-mod.model';
import { Template, TemplateType } from 'models/template.model';

@Component({
  selector: 'app-edit-article-in-offer-form',
  templateUrl: './edit-article-in-offer-form.component.html',
  styleUrls: ['./edit-article-in-offer-form.component.scss']
})
export class EditArticleInOfferFormComponent implements OnInit {

  form!: UntypedFormGroup;

  formReady = false;

  position!: CustomLine;

  articleData!: any;

  isDealer!: boolean;

  detectChanges = false;

  mnrPlaceholder = '';

  eanPlaceholder = '';

  hasChange = false;

  productId = '';

  offer!: OfferBase;

  dropdownData: Record<string, any> = {
    positionTemplates: []
  };

  isCollapsed = false;

  get articleForm(): UntypedFormGroup {
    return this.form.controls.article as UntypedFormGroup;
  }

  constructor(
    protected fb: UntypedFormBuilder,
    private coreDataService: CoreDataService,
    private storeService: StoreService,
    private posCustomPropertiesService: PositionCustomPropertiesService
  ) { }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    combineLatest([
      this.storeService.getCurrentOffer(),
      this.storeService.getCustomPosition(),
      this.storeService.isCurrentAccountDealer()
    ])
    .pipe(take(1))
    .subscribe(([offer, position, isDealer]: [OfferBase, CustomLine, boolean]) => {
      this.offer = offer;
      this.position = position;
      this.isDealer = isDealer;

      if (this.isDealer) {
        this.productId = this.posCustomPropertiesService.getProductID(this.position);
      }

      const customData = this.posCustomPropertiesService.getLineArticleConfiguration(this.position);

      if (!this.isDealer && !customData) {
        this.articleData = {
          name: this.position.customProductName || this.posCustomPropertiesService.getLineKurztext(this.position),
          description: this.position.customProductText || this.posCustomPropertiesService.getFreeArticleDescription(this.position)
        }
      } else {
        if (customData) {
          this.articleData = JSON.parse(customData);
          if (!this.isDealer && (this.articleData?.mnr || this.articleData?.ean)) {
            this.detectChanges = true;
            this.mnrPlaceholder = this.articleData?.mnr || '';
            this.eanPlaceholder = this.articleData?.ean || '';
          }
        } else {
          this.articleData = this.position?.article;
          if (this.articleData?.mnr || this.articleData?.ean) {
            this.detectChanges = true;
            this.mnrPlaceholder = this.articleData?.mnr || '';
            this.eanPlaceholder = this.articleData?.ean || '';
          }
        }
      }

      this.setupForm();
      this.getPositionTemplates();
    });
  }

  setupForm(): void {
    this.form = this.fb.group({
      article: this.fb.group({
        name: [this.articleData?.name || '', [Validators.required]],
        description: [this.articleData?.description || ''],
        mnr: [(this.articleData?.mnr ? this.articleData.mnr : '')],
        ean: [(this.articleData?.ean ? this.articleData.ean :'')],
        category: [this.articleData?.category?.displayName || ''],
        unit: [this.articleData?.unit || ''],
        width: [this.articleData?.width || ''],
        height: [this.articleData?.height || ''],
        depth: [this.articleData?.depth || ''],
        direction: [this.articleData?.direction || ''],
        listPrice: [
          this.position?.pricing?.listPrice.value || 0,
          [
            Validators.required,
            Validators.min(VALIDATORS.MIN.ARTICLE_LIST_PRICE),
            Validators.max(VALIDATORS.MAX.ARTICLE_LIST_PRICE)
          ]
        ],
        purchasePrice: [
          this.position?.pricing?.basePrice.value || 0,
          [
            Validators.required,
            Validators.min(VALIDATORS.MIN.ARTICLE_PURCHASE_PRICE),
            Validators.max(VALIDATORS.MAX.ARTICLE_PURCHASE_PRICE)
          ]
        ],
        isCustom: [true],
        id: [this.productId || '']
      }),
      positionTemplate: [null],
      text: [this.position?.descriptiveText || '']
    }, { updateOn: 'change' });

    this.detectFormChanges();
    this.formReady = true;
    this.storeService.setFormReady(this.formReady);
  }

  detectFormChanges(): void {
    if (this.detectChanges) {
      this.detectArticleFormChanges();
    }

    this.form.controls.positionTemplate.valueChanges
    .subscribe((value) => {
      this.form.controls.text.setValue(value, { emitEvent: false });
    });
  }

  detectArticleFormChanges(): void {
    const formKeys = Object.keys(this.articleForm.controls);
    const articleFormChanges$ = merge(
      ...formKeys.map(key =>
        this.articleForm.get(key)!.valueChanges
        .pipe(
          map((value) => ({ 'key': key, 'value': value }))
        )
      )
    );

    articleFormChanges$.subscribe((field: any) => {
      if (field.key === 'mnr'){
        this.mnrPlaceholder = '';
      } else if (field.key === 'ean'){
        this.eanPlaceholder = '';
      } else {
        if (this.mnrPlaceholder !== '') {
          this.articleForm.controls.mnr.setValue('', { emitEvent: false })
        }

        if (this.eanPlaceholder !== '') {
          this.articleForm.controls.ean.setValue('', { emitEvent: false })
        }
      }
    });
  }

  private getPositionTemplates(): void {
    this.coreDataService.getTemplates(100, TemplateType.POSITION)
    .subscribe((templates: Template[]) => {
      this.dropdownData.positionTemplates = templates;
    });
  }

  protected readonly svgIconSizeMod = SvgIconSizeMod
}
